import {
  Ban,
  CalendarX2,
  ChevronDown,
  FileText,
  GitMerge,
  History,
  type LucideIcon,
  Pin,
  TriangleAlert,
  Undo2,
  Unlink,
} from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible'
import type { CaseEventKind } from '@/services/api/case-events'
import { formatHistoryTime, formatHistoryTimestamp } from '@/utils/date'
import type { EntityHistoryRow } from './EntityHistoryCard'
import { factRowText } from './fact-row-text'
import { HistoryEventDetail } from './HistoryEventDetail'
import type { HistoryActions } from './history-detail'
import { identityRowText } from './identity-row-text'
import { historyRowText, type PropertyChangeRow } from './row-text'

const HISTORY = 'investigations.entityDetail.history'

/** Every kind of the closed vocabulary has a label: a new one is a compile error until it does. */
const KIND_LABELS: Record<CaseEventKind, string> = {
  source_received: `${HISTORY}.kinds.source_received`,
  entity_mentioned: `${HISTORY}.kinds.entity_mentioned`,
  relationship_added: `${HISTORY}.kinds.relationship_added`,
  relationship_removed: `${HISTORY}.kinds.relationship_removed`,
  property_changed: `${HISTORY}.kinds.property_changed`,
  entities_merged: `${HISTORY}.kinds.entities_merged`,
  merge_reverted: `${HISTORY}.kinds.merge_reverted`,
  pair_dismissed: `${HISTORY}.kinds.pair_dismissed`,
  fact_asserted: `${HISTORY}.kinds.fact_asserted`,
  fact_invalidated: `${HISTORY}.kinds.fact_invalidated`,
  entity_retired: `${HISTORY}.kinds.entity_retired`,
  source_superseded: `${HISTORY}.kinds.source_superseded`,
  assessment_changed: `${HISTORY}.kinds.assessment_changed`,
}

/** The replaced value came from another source (ADR-0093): marked, and named on hover. */
function ConflictMark({ property }: { property: PropertyChangeRow }) {
  const { t } = useTranslation()
  const label = property.pinned
    ? t(`${HISTORY}.rows.pinnedConflict`)
    : t(`${HISTORY}.rows.conflict`, {
        document: property.previousDocumentName ?? t('investigations.entityDetail.unknownDocument'),
      })
  return (
    <span role="img" aria-label={label} title={label} className="shrink-0">
      <TriangleAlert aria-hidden className="size-3 text-warning" />
    </span>
  )
}

/** A row's icon by kind: a source, or a change of who the entity is; any other kind reads as history. */
const ROW_ICONS: Partial<Record<CaseEventKind, LucideIcon>> = {
  entity_mentioned: FileText,
  entities_merged: GitMerge,
  merge_reverted: Undo2,
  pair_dismissed: Unlink,
  fact_asserted: Pin,
  fact_invalidated: CalendarX2,
  entity_retired: Ban,
}

export function HistoryEntry({ row, actions }: { row: EntityHistoryRow; actions: HistoryActions }) {
  const { t, i18n } = useTranslation()
  const what =
    identityRowText(row, t) ??
    factRowText(row, t, i18n.language) ??
    historyRowText(row, t) ??
    t(row.kind ? KIND_LABELS[row.kind] : `${HISTORY}.kinds.unknown`)
  const Icon = (row.kind && ROW_ICONS[row.kind]) || History
  return (
    <Collapsible className="rounded-md border border-border/50 bg-card/40">
      <CollapsibleTrigger asChild>
        <Button
          variant="ghost"
          className="group h-auto w-full items-start justify-start gap-2 rounded-md px-2 py-3 text-left whitespace-normal"
        >
          <Icon aria-hidden className="mt-0.5 size-3.5 shrink-0 text-muted-foreground" />
          <span className="line-clamp-2 min-w-0 flex-1 text-xs leading-relaxed">{what}</span>
          {row.property?.conflict && <ConflictMark property={row.property} />}
          <time
            dateTime={row.learnedAt}
            title={formatHistoryTimestamp(row.learnedAt, i18n.language)}
            className="mt-0.5 shrink-0 text-2xs tabular-nums text-muted-foreground"
          >
            {formatHistoryTime(row.learnedAt, i18n.language)}
          </time>
          <ChevronDown
            aria-hidden
            className="mt-0.5 size-3.5 shrink-0 text-muted-foreground transition-transform group-data-[state=open]:rotate-180"
          />
        </Button>
      </CollapsibleTrigger>
      <CollapsibleContent>
        <HistoryEventDetail row={row} actions={actions} description={what} />
      </CollapsibleContent>
    </Collapsible>
  )
}
